import { Camp } from "./types";
import { parseLocalDate } from "./dateUtils";

export class CampFormatUtils {

  static formatTime(time: string | null): string {
    if (!time) return 'TBD';
    return new Date(`2000-01-01T${time}`).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  }

  static formatTimeRange(startTime: string | null, endTime: string | null): string {
    return `${this.formatTime(startTime)} - ${this.formatTime(endTime)}`;
  }

  static formatDate(dateString: string, weekday: boolean = true): string {
    // Parse the date string as local time to avoid timezone issues
    const date = parseLocalDate(dateString);

    return date.toLocaleDateString('en-US', {
      weekday: weekday ? 'short' : undefined,
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  }

  static formatDateRange(startDate: string, endDate: string, showWeekday: boolean = true): string {
    if (startDate === endDate) {
      return this.formatDate(startDate, showWeekday);
    }
    return `${this.formatDate(startDate, false)} - ${this.formatDate(endDate, false)}`;
  }

  static formatAgeRange(minAge?: number | null, maxAge?: number | null): string {
    if (minAge && maxAge) {
      return `${minAge}-${maxAge} years`;
    } else if (minAge) {
      return `${minAge}+ years`;
    } else if (maxAge) {
      return `Up to ${maxAge} years`;
    }
    return 'All ages';
  }

  static getPriceDisplay(camp: Camp): string {
    const { price, timeframe } = this.getPriceParts(camp);

    // Handle N/A case
    if (price === 'N/A') {
      return 'N/A';
    }

    // Otherwise add $ and timeframe
    return timeframe ? `$${price} / ${timeframe}` : `$${price}`;
  }

  static getPriceParts(camp: Camp): { price: string; timeframe: string } {
    if (!camp.price) {
      return { price: 'N/A', timeframe: '' };
    }

    const price = camp.price;
    // Get sessions count from the first camp date
    const campDate = camp.camp_dates[0];
    if (!campDate) {
      return { price: `${price}`, timeframe: '' };
    }
    const sessionsCount = campDate.sessions ? campDate.sessions.length : 1;
    let dayText = sessionsCount === 1 ? 'day' : 'days';

    // Adjust for half-day camps (4 hours or less, single day)
    if (sessionsCount === 1 && camp.start_time && camp.end_time) {
      try {
        // Parse times to calculate duration
        const startTime = new Date(`1970-01-01T${camp.start_time}`);
        const endTime = new Date(`1970-01-01T${camp.end_time}`);
        const durationHours = (endTime.getTime() - startTime.getTime()) / (1000 * 60 * 60);

        // Check for invalid duration (NaN or negative)
        if (isNaN(durationHours) || durationHours < 0) {
          throw new Error('Invalid duration');
        }

        if (durationHours <= 4) {
          return {
            price: `${price}`,
            timeframe: 'half day'
          };
        }
      } catch (error) {
        // If parsing fails, keep original timeframe
      }
    }

    return {
      price: `${price}`,
      timeframe: `${sessionsCount} ${dayText}`
    };
  }
}
